/**
 * Minimum Estimate Warning Modal
 * Displays when quote total is below minimum service amount
 */
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { AlertTriangle, Phone } from "lucide-react";

export default function MinimumWarningModal({
  open,
  onOpenChange,
  minimumEstimate,
  currentTotal,
  upsellSuggestions,
  onAddService,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="minimum-warning-modal">
        <DialogHeader>
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-white" />
          </div>
          <DialogTitle className="text-center text-xl">
            Minimum Service Amount Not Met
          </DialogTitle>
          <DialogDescription className="text-center">
            Our minimum service amount is <strong className="text-slate-900">${minimumEstimate}</strong>.
            Your current estimate is <strong className="text-slate-900">${currentTotal}</strong>.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          <p className="text-sm text-slate-600 text-center">
            Please add additional services to meet the minimum, or contact us to discuss your needs.
          </p>
          
          <div className="flex items-center justify-center gap-2 p-3 bg-blue-50 rounded-lg">
            <Phone className="w-5 h-5 text-blue-600" />
            <a 
              href="tel:6304487014" 
              className="font-semibold text-blue-600 hover:text-blue-800"
            >
              (630) 448-7014
            </a>
          </div>
          
          {upsellSuggestions.length > 0 && (
            <div className="space-y-2 pt-2">
              <p className="text-sm font-medium text-slate-700 text-center">Quick add to meet minimum:</p>
              {upsellSuggestions.slice(0, 2).map((service) => (
                <button
                  key={service.service_id}
                  onClick={() => onAddService(service)}
                  className="w-full p-3 bg-gradient-to-r from-emerald-50 to-white border border-emerald-200 rounded-lg hover:border-emerald-400 transition-all text-left"
                  data-testid={`minimum-add-${service.service_id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-900">{service.service_name}</span>
                    <span className="text-emerald-600 font-bold">+${service.bundlePrice}</span>
                  </div>
                  {service.service_id === 'windows' && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Screens must be removed by owner
                    </p>
                  )}
                  {service.service_id === 'gutters' && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Open gutters only - no screens
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
            data-testid="minimum-back-btn"
          >
            Go back and add services
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
